import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Check, Volume2, VolumeX, Play, ChevronDown, ChevronUp, User, Users } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { videoAPI } from '../../../lib/api';

/**
 * Step 5: does the user want AI voiceover on their auto-posts? And if
 * yes, *which* voice should speak?
 *
 * Layout (top to bottom):
 *   1. Header + a 9:16 preview of New Listing Story rendered with the
 *      default voice. So the page opens with a representative sample.
 *   2. Yes/No cards for the binary "use voiceover" decision.
 *   3. Voice picker (only when Yes). 6 curated voices fetched from
 *      `/api/voice-presets`. Each card plays a 7-second sample MP4 of
 *      the same demo line in that voice. Tap = play, tap-elsewhere =
 *      switch source. The card with the active selection is the one
 *      that gets persisted as `wizardData.voice_id`.
 *   4. Advanced expander. Paste a raw ElevenLabs voice_id for power
 *      users who already cloned a voice or want one outside the curated
 *      set. Stored verbatim into `wizardData.voice_id`.
 *
 * Props
 *   enableVoiceover   boolean. Yes/no flag
 *   voiceId           string . Selected curated key OR raw voice_id
 *   onChange({ enableVoiceover, voiceId }). Committed back to wizard
 */

// Showcase video URL the preview pulls from. Pinned to the New Listing
// Story render because (a) it's the canonical "neu am Markt" listing
// announcement most agents will actually post, and (b) the bake-in voice
// line in render_custom_content.py is structured around exactly that
// scenario. Cache-buster appended on each re-render via apply_showcase.
//
// URL is built from REACT_APP_BACKEND_URL so a backend migration moves
// this asset without a frontend rebuild. See LandingPage for the same
// pattern applied to the marketing reels.
const PREVIEW_VIDEO_URL =
  `${process.env.REACT_APP_BACKEND_URL || ''}/uploads/showcase/61881343-6963-4d9e-9b50-6a63f2d4af7b.mp4?v=1777453016`;

export default function VoiceoverStep({ enableVoiceover, voiceId, onChange }) {
  const { t, lang } = useTranslation();
  const videoRef = useRef(null);
  const sampleAudioRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);

  // Voice catalog. Fetched once on mount. Empty list while loading
  // doesn't block the rest of the step; the user can still pick yes/no.
  const [voices, setVoices] = useState([]);
  const [defaultKey, setDefaultKey] = useState('matilda');
  const [voicesLoading, setVoicesLoading] = useState(true);

  // Currently-playing sample. Tracked so we can show a Pause icon on the
  // active card and stop one when the user starts another.
  const [playingKey, setPlayingKey] = useState(null);

  // Advanced field state. Hidden behind the chevron. Most users never
  // touch it. The text input mirrors `voiceId` only when it's NOT one of
  // the curated keys (so flipping between curated voices doesn't keep
  // overwriting the custom field).
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [customVoiceId, setCustomVoiceId] = useState('');

  // Helper: emit the combined update upward in one shot. The parent
  // wizard owns both flags as a single update; that keeps the back-button
  // semantics clean.
  const emit = (patch) => {
    onChange?.({
      enableVoiceover: enableVoiceover,
      voiceId: voiceId,
      ...patch,
    });
  };

  // Fetch curated voices once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await videoAPI.getVoicePresets();
        if (cancelled) return;
        setVoices(res.data?.voices || []);
        setDefaultKey(res.data?.default_key || 'matilda');
      } catch (_) {
        // Non-fatal. Picker just shows an empty grid + the advanced
        // field. The yes/no decision still works.
      } finally {
        if (!cancelled) setVoicesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Press-and-hold needs a global release listener. If the user drags
  // their finger off the button mid-hold, the button's own onTouchEnd
  // never fires (because the touch is technically off it). Listening on
  // window catches release-anywhere. Same for mouseup if the cursor
  // wandered off mid-click.
  useEffect(() => {
    const release = () => {
      if (playingKey) stopSample();
    };
    window.addEventListener('mouseup', release);
    window.addEventListener('touchend', release);
    window.addEventListener('touchcancel', release);
    return () => {
      window.removeEventListener('mouseup', release);
      window.removeEventListener('touchend', release);
      window.removeEventListener('touchcancel', release);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playingKey]);

  // Initialize the custom-id text field if the user already has a voice
  // saved that ISN'T one of the curated keys (i.e. they pasted a raw id
  // earlier). Surfaces the value in the advanced field on revisit.
  useEffect(() => {
    if (!voiceId) return;
    const isCurated = voices.some((v) => v.key === voiceId);
    if (!isCurated) {
      setCustomVoiceId(voiceId);
      setAdvancedOpen(true);
    }
  }, [voiceId, voices]);

  const toggleMute = () => {
    if (!videoRef.current) return;
    const next = !isMuted;
    videoRef.current.muted = next;
    setIsMuted(next);
    if (!next) {
      // If we just unmuted, start over so the user gets the full voice
      // line. Otherwise they catch the middle of a sentence.
      try { videoRef.current.currentTime = 0; videoRef.current.play(); } catch (_) {}
    }
  };

  // Stop preview audio + sample audio when user switches off this step.
  // (Sample audio is played via a single shared <audio> element so we
  // don't have to manage a ref-per-card.)
  const stopAllAudio = () => {
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch (_) {}
      videoRef.current.muted = true;
      setIsMuted(true);
    }
    if (sampleAudioRef.current) {
      try { sampleAudioRef.current.pause(); } catch (_) {}
    }
    setPlayingKey(null);
  };

  const pickYesNo = (yes) => {
    stopAllAudio();
    if (yes) {
      // Yes path: ensure a voice is selected. Fall back to the default
      // when the user hasn't picked one yet so the next step never sees
      // a half-configured state.
      const nextVoice = voiceId || defaultKey;
      emit({ enableVoiceover: true, voiceId: nextVoice });
    } else {
      // No path: keep voiceId as-is (so flipping yes/no doesn't lose
      // their pick). Just flag voiceover as disabled.
      emit({ enableVoiceover: false });
    }
  };

  const pickVoice = (key) => {
    // Selecting a curated voice clears the custom field. They're
    // mutually exclusive on the wire (one `voice_id` value persists).
    setCustomVoiceId('');
    emit({ enableVoiceover: true, voiceId: key });
  };

  // Press-and-hold playback. Plain tap-to-toggle was bad UX on phones –
  // there's no obvious affordance to stop the loop and the audio kept
  // playing as users walked away from the page. Hold-to-listen mirrors
  // the way intercoms / push-to-talk work, which everyone already
  // intuits. Single function for both press + release so we manage the
  // shared audio element from one place.
  const startSample = (voice) => {
    if (!sampleAudioRef.current) return;
    // Mid-flight switch: if a different sample is currently playing,
    // pause it before starting the new one.
    try { sampleAudioRef.current.pause(); } catch (_) {}
    sampleAudioRef.current.src = voice.sample_url;
    sampleAudioRef.current.currentTime = 0;
    // Browsers may reject autoplay if the sample wasn't user-triggered
    // by a recent gesture. But we ARE inside a user gesture (mousedown
    // / touchstart), so this resolves cleanly. Catch on principle so a
    // gesture-rejected play doesn't blow up the React render.
    sampleAudioRef.current.play().catch(() => {});
    setPlayingKey(voice.key);
  };

  const stopSample = () => {
    if (!sampleAudioRef.current) return;
    try { sampleAudioRef.current.pause(); } catch (_) {}
    setPlayingKey(null);
  };

  // Commit the custom voice_id when the input loses focus or the user
  // hits Enter. Avoids spamming wizardData updates on every keystroke.
  const commitCustomVoice = () => {
    const trimmed = customVoiceId.trim();
    if (!trimmed) return;
    emit({ enableVoiceover: true, voiceId: trimmed });
  };

  const yesNoOptions = [
    {
      key: true,
      icon: <Mic className="w-7 h-7" />,
      title: t('onboarding.step5.yesTitle') || 'Ja, KI-Stimme behalten',
      body:
        t('onboarding.step5.yesBody') ||
        'Wir sprechen deine Immobilien automatisch mit deinem Namen und Markennamen. Gut für Neuheiten-Posts.',
      accent: 'emerald',
    },
    {
      key: false,
      icon: <MicOff className="w-7 h-7" />,
      title: t('onboarding.step5.noTitle') || 'Lieber nur Musik',
      body:
        t('onboarding.step5.noBody') ||
        'Kein Voiceover. Posts sind ruhiger und wirken oft auf TikTok/Reels natürlicher.',
      accent: 'slate',
    },
  ];

  // Determine which voice is currently the "active" pick on the grid.
  // If voiceId is null/empty, default key wins (so the page doesn't open
  // with zero selected when the user lands on the Yes path).
  const activeVoiceKey =
    enableVoiceover && voiceId && voices.some((v) => v.key === voiceId)
      ? voiceId
      : enableVoiceover
        ? defaultKey
        : null;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex p-4 bg-[#e9efe7] rounded-2xl mb-4">
          <Mic className="w-10 h-10 text-[#1f4d3a]" />
        </div>
        <h2 className="text-2xl font-bold text-[#23211a] mb-1">
          {t('onboarding.step5.title') || 'KI-Stimme verwenden?'}
        </h2>
        <p className="text-sm text-[#6f6a5c] max-w-md mx-auto">
          {t('onboarding.step5.subtitleWithPreview') ||
            'Hör kurz rein, wie die KI-Stimme klingt. Dann entscheide, ob sie auf deine Auto-Posts soll.'}
        </p>
      </div>

      {/* Preview clip. Full-bleed 9:16 with a mute toggle in the corner.
          Default muted so the page is quiet on entry; tap to unmute. */}
      <div className="relative mx-auto w-full max-w-[220px] aspect-[9/16] rounded-xl overflow-hidden bg-black border border-[#e3dccb]">
        <video
          ref={videoRef}
          src={PREVIEW_VIDEO_URL}
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onClick={toggleMute}
        />
        <button
          type="button"
          onClick={toggleMute}
          className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 bg-black/65 backdrop-blur px-3 py-1.5 rounded-full text-xs text-white border border-white/15 hover:bg-black/80 transition"
          aria-label={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? (
            <>
              <VolumeX className="w-3.5 h-3.5" />
              {t('onboarding.step5.tapToHear') || 'Stimme anhören'}
            </>
          ) : (
            <>
              <Volume2 className="w-3.5 h-3.5" />
              {t('onboarding.step5.muted') || 'Leise stellen'}
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {yesNoOptions.map((o) => {
          const isSelected = enableVoiceover === o.key;
          const color = o.accent === 'emerald' ? 'emerald' : 'slate';
          const selectedClasses =
            color === 'emerald'
              ? 'border-[#1e7a4f] bg-[#e4f0e8] shadow-[0_0_0_4px_rgba(16,185,129,0.1)]'
              : 'border-[#b5ad99] bg-[#b5ad99]/10 shadow-[0_0_0_4px_rgba(148,163,184,0.1)]';
          return (
            <button
              key={String(o.key)}
              type="button"
              onClick={() => pickYesNo(o.key)}
              className={`text-left p-5 rounded-xl border-2 transition-all ${
                isSelected
                  ? selectedClasses
                  : 'border-[#d8d0bd] bg-[#fffdf7] hover:border-[#1f4d3a]/50'
              }`}
            >
              <div className="flex items-start justify-between">
                <div
                  className={`p-2.5 rounded-lg ${
                    color === 'emerald' ? 'bg-[#d6e8dd] text-[#1e7a4f]' : 'bg-[#d8d0bd] text-[#4a463b]'
                  }`}
                >
                  {o.icon}
                </div>
                {isSelected && (
                  <Check className={`w-5 h-5 ${color === 'emerald' ? 'text-[#1e7a4f]' : 'text-[#4a463b]'}`} />
                )}
              </div>
              <p className="font-semibold text-[#23211a] mt-3">{o.title}</p>
              <p className="text-xs text-[#6f6a5c] mt-1.5 leading-relaxed">{o.body}</p>
            </button>
          );
        })}
      </div>

      {/* Voice picker. Only shown when the user said yes. Fades in via
          plain conditional render rather than a transition because step
          transitions already animate the wizard panel. */}
      {enableVoiceover && (
        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[#23211a]">
              {t('onboarding.step5.voicePickerTitle') || 'Stimme wählen'}
            </h3>
            <span className="text-xs text-[#8a8474]">
              {t('onboarding.step5.voicePickerHint') || 'Tippe zum Anhören'}
            </span>
          </div>

          {voicesLoading ? (
            <div className="text-xs text-[#8a8474] text-center py-4">…</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {voices.map((voice) => {
                const isActive = activeVoiceKey === voice.key;
                const isPlaying = playingKey === voice.key;
                const Icon = voice.gender === 'female' ? User : Users;
                const description =
                  lang === 'en' ? voice.description_en : voice.description_de;
                return (
                  <div
                    key={voice.key}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      isActive
                        ? 'border-[#1f4d3a] bg-[#e9efe7] shadow-[0_0_0_3px_rgba(139,92,246,0.1)]'
                        : 'border-[#d8d0bd]/80 bg-[#f2ecdf] hover:border-[#1f4d3a]/50'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => pickVoice(voice.key)}
                      className="w-full text-left"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className={`p-1.5 rounded-md shrink-0 ${
                            voice.gender === 'female'
                              ? 'bg-pink-500/20 text-pink-300'
                              : 'bg-blue-500/20 text-blue-300'
                          }`}>
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <span className="text-sm font-medium text-[#23211a] truncate">
                            {voice.name}
                          </span>
                        </div>
                        {isActive && <Check className="w-4 h-4 text-[#1f4d3a] shrink-0" />}
                      </div>
                      <p className="text-[11px] text-[#6f6a5c] mt-1.5 leading-tight line-clamp-2">
                        {description}
                      </p>
                    </button>
                    {/* Press-and-hold preview button. Lives in its own
                        clickable area so triggering the sample doesn't
                        also re-select the voice. User can A/B before
                        committing. Listens to mouse + touch + pointer
                        events; `release-anywhere` is handled by the
                        global mouseup/touchend listener wired in
                        useEffect below so dragging off the button still
                        stops audio cleanly.
                        contextMenu prevention stops the long-press
                        share/save menu on mobile from interrupting. */}
                    <button
                      type="button"
                      onMouseDown={(e) => { e.stopPropagation(); startSample(voice); }}
                      onTouchStart={(e) => { e.stopPropagation(); startSample(voice); }}
                      onContextMenu={(e) => e.preventDefault()}
                      onClick={(e) => e.stopPropagation()}
                      // Keyboard accessibility: Space/Enter mimics
                      // press-and-hold for ~3s so screen-reader users
                      // can preview without holding.
                      onKeyDown={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          if (playingKey !== voice.key) startSample(voice);
                        }
                      }}
                      onKeyUp={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                          e.preventDefault();
                          stopSample();
                        }
                      }}
                      className={`mt-2 w-full inline-flex items-center justify-center gap-1.5 px-2 py-1 rounded-md text-[11px] transition select-none ${
                        isPlaying
                          ? 'bg-[#1f4d3a]/30 text-[#23211a] ring-1 ring-[#1f4d3a]/60'
                          : 'bg-[#efe9db] hover:bg-[#efe9db] text-[#4a463b]'
                      }`}
                      style={{ touchAction: 'manipulation', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}
                    >
                      {isPlaying ? (
                        <>
                          <Volume2 className="w-3 h-3 animate-pulse" />
                          {t('onboarding.step5.holding') || 'Halten…'}
                        </>
                      ) : (
                        <>
                          <Play className="w-3 h-3" />
                          {t('onboarding.step5.holdToHear') || 'Halten zum Anhören'}
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Shared audio element. One per page is enough since only
              one sample plays at a time. Hidden so the browser's native
              controls don't show up. */}
          <audio
            ref={sampleAudioRef}
            preload="none"
            onEnded={() => setPlayingKey(null)}
          />

          {/* Advanced. Power users with their own ElevenLabs voice_id
              (e.g. they cloned their own voice over there) can paste it
              here. We don't validate against ElevenLabs; if the id is
              wrong Creatomate fails the render and the user gets a
              normal error toast. */}
          <button
            type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs text-[#6f6a5c] hover:text-[#23211a] transition"
          >
            {advancedOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {t('onboarding.step5.advancedTitle') || 'Erweitert: Eigene Stimme'}
          </button>
          {advancedOpen && (
            <div className="rounded-lg border border-[#e3dccb] bg-[#f2ecdf] p-3 space-y-2.5">
              <p className="text-[11px] text-[#4a463b] leading-relaxed font-medium">
                Eigene Stimme verwenden (z.&nbsp;B. dein eigener Voice-Clone)
              </p>
              <ol className="text-[11px] text-[#6f6a5c] leading-relaxed list-decimal pl-4 space-y-1">
                <li>Kostenloses Konto auf <a href="https://elevenlabs.io" target="_blank" rel="noreferrer" className="text-[#1f4d3a] underline underline-offset-2">elevenlabs.io</a> erstellen.</li>
                <li><span className="text-[#4a463b]">Voices → Add a new voice → Instant Voice Clone</span>, ca. 1&nbsp;Minute Audio von dir hochladen.</li>
                <li>Die fertige Stimme öffnen und <span className="text-[#4a463b]">„Copy Voice ID"</span> anklicken.</li>
                <li>Die kopierte voice_id unten einfügen.</li>
              </ol>
              <input
                type="text"
                value={customVoiceId}
                onChange={(e) => setCustomVoiceId(e.target.value)}
                onBlur={commitCustomVoice}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitCustomVoice(); } }}
                placeholder="z.B. XrExE9yKIg1WjnnlVkGX"
                className="w-full bg-[#f7f3ea]/60 border border-[#d8d0bd] rounded-md px-3 py-1.5 text-xs text-[#23211a] placeholder:text-[#9c9585] focus:outline-none focus:ring-2 focus:ring-[#1f4d3a]/40 font-mono"
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
              />
              <p className="text-[10px] text-[#8a8474] leading-relaxed">
                Die voice_id sieht aus wie <span className="font-mono text-[#6f6a5c]">XrExE9yKIg1WjnnlVkGX</span>. Eigene Stimmen sind in <span className="text-[#4a463b]">Autopilot &amp; Team</span> verfügbar.
              </p>
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-[#8a8474] text-center">
        {t('onboarding.step5.footer') || 'Du kannst diese Einstellung jederzeit in den Auto-Post-Optionen ändern.'}
      </p>
    </div>
  );
}
